import { Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { PublicLayout } from '@/app/layouts/PublicLayout';
import { AppLayout } from '@/app/layouts/AppLayout';
import {
  PublicOnly,
  RequireActorTypes,
  RequireAuth,
  RequirePermission,
  RequireWorkspaceAccess,
  WorkspaceHomeRoute,
} from './route-guards';
import { ROUTES } from './route-paths';
import { SuspenseFallback } from './SuspenseFallback';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { lazyWithRetry } from './lazyWithRetry';

// Landing is eager-loaded (it's the root). It carries its own Stitch header +
// footer, so it is NOT wrapped in PublicLayout. Auth + placeholders are lazy.
// Public landing + auth are the exact Stitch rebuild (DEC-051).
//
// Lazy routes use lazyWithRetry so a transient dynamic-import failure (stale
// chunk after a deploy, or a Vite dep re-optimization in dev) self-heals via a
// one-shot reload instead of dead-ending; unrecoverable cases fall through to
// the route `errorElement` (RouteErrorBoundary).
import LandingPage from '@/features/landing/pages/LandingPage';

const DashboardPage = lazyWithRetry(
  () => import('@/features/start-up/dashboard/pages/DashboardPage'),
);
const CandidateProfilePage = lazyWithRetry(
  () => import('@/features/candidate-management/candidates/pages/CandidateProfilePage'),
);
const AccountPage = lazyWithRetry(
  () => import('@/features/identity-management/account/pages/AccountPage'),
);
const NotFoundPage = lazyWithRetry(
  () => import('@/features/landing/pages/NotFoundPage'),
);
const EarlyAccessPage = lazyWithRetry(
  () => import('@/features/early-access/pages/EarlyAccessPage'),
);
const PlansPlaceholderPage = lazyWithRetry(
  () => import('@/features/landing/pages/PlansPlaceholderPage'),
);
const UnauthorizedPage = lazyWithRetry(
  () => import('@/features/landing/pages/UnauthorizedPage'),
);
const ServiceDownPage = lazyWithRetry(
  () => import('@/features/landing/pages/ServiceDownPage'),
);
const LoginPage = lazyWithRetry(
  () => import('@/features/identity-management/auth/pages/LoginPage'),
);
const OAuthCallbackPage = lazyWithRetry(
  () => import('@/features/identity-management/auth/pages/OAuthCallbackPage'),
);
const InviteAcceptPage = lazyWithRetry(
  () => import('@/features/identity-management/invitations/pages/InviteAcceptPage'),
);

// Reserved PRD modules surfaced as navigable previews inside the App Shell.
const SmartProfilePreviewPage = lazyWithRetry(
  () => import('@/features/workspace-previews/pages/SmartProfilePreviewPage'),
);
const JobMatchesPreviewPage = lazyWithRetry(
  () => import('@/features/workspace-previews/pages/JobMatchesPreviewPage'),
);
const ApplicationsPreviewPage = lazyWithRetry(
  () => import('@/features/workspace-previews/pages/ApplicationsPreviewPage'),
);
const CompanyPlaceholderPage = lazyWithRetry(
  () => import('@/features/workspace-previews/pages/CompanyPlaceholderPage'),
);
const EmployerDashboardPage = lazyWithRetry(
  () => import('@/features/employer-management/hiring/pages/EmployerDashboardPage'),
);
const CompanyApplicationsPage = lazyWithRetry(
  () => import('@/features/employer-management/hiring/pages/CompanyApplicationsPage'),
);
const CandidateSearchPage = lazyWithRetry(
  () => import('@/features/employer-management/hiring/pages/CandidateSearchPage'),
);
const CompanyProfilePage = lazyWithRetry(
  () => import('@/features/employer-management/hiring/pages/CompanyProfilePage'),
);
const SettingsPlaceholderPage = lazyWithRetry(
  () => import('@/features/workspace-previews/pages/SettingsPlaceholderPage'),
);
const AnalyticsPlaceholderPage = lazyWithRetry(
  () => import('@/features/workspace-previews/pages/AnalyticsPlaceholderPage'),
);
const IntegrationsPlaceholderPage = lazyWithRetry(
  () => import('@/features/workspace-previews/pages/IntegrationsPlaceholderPage'),
);
const EarlyAccessAdminPage = lazyWithRetry(
  () => import('@/features/system-management/early-access-admin/pages/EarlyAccessAdminPage'),
);
const AdminDashboardPage = lazyWithRetry(
  () => import('@/features/system-management/admin/pages/AdminDashboardPage'),
);
const AdminUsersPage = lazyWithRetry(
  () => import('@/features/system-management/admin/pages/AdminUsersPage'),
);
const AdminRolesPage = lazyWithRetry(
  () => import('@/features/system-management/admin/pages/AdminRolesPage'),
);
const AdminSettingsPage = lazyWithRetry(
  () => import('@/features/system-management/admin/pages/AdminSettingsPage'),
);
const AdminAiUsagePage = lazyWithRetry(
  () => import('@/features/system-management/admin/pages/AdminAiUsagePage'),
);
const AdminPlansPage = lazyWithRetry(
  () => import('@/features/system-management/admin/pages/AdminPlansPage'),
);
const AuditLogPage = lazyWithRetry(
  () => import('@/features/system-management/audit/pages/AuditLogPage'),
);

export const routes: RouteObject[] = [
  // Stitch landing — renders its own header/footer (no PublicLayout chrome).
  {
    path: ROUTES.landing,
    element: <LandingPage />,
    errorElement: <RouteErrorBoundary />,
  },
  // Stitch split-screen auth — own full-screen shell.
  {
    path: ROUTES.auth.login,
    errorElement: <RouteErrorBoundary />,
    element: (
      <PublicOnly>
        <Suspense fallback={<SuspenseFallback />}>
          <LoginPage />
        </Suspense>
      </PublicOnly>
    ),
  },
  {
    // Workerize is invite-only during early access — open password signup is
    // disabled; route everyone to the waitlist form.
    path: ROUTES.auth.register,
    errorElement: <RouteErrorBoundary />,
    element: <Navigate to={ROUTES.register} replace />,
  },
  {
    // Public invitation welcome / accept page. No guard: the invitee is not
    // authenticated yet — they're creating their account from the email link.
    path: ROUTES.inviteAccept,
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <InviteAcceptPage />
      </Suspense>
    ),
  },
  {
    // OAuth landing — persists the refresh token from the callback fragment and
    // boots the session. No guard: the user isn't authenticated yet on arrival.
    path: ROUTES.auth.callback,
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <OAuthCallbackPage />
      </Suspense>
    ),
  },
  // Placeholder CTA destinations + not-found keep the foundation PublicLayout.
  {
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: ROUTES.register,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <EarlyAccessPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.plans,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <PlansPlaceholderPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.unauthorized,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <UnauthorizedPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.serviceDown,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <ServiceDownPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.notFound,
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: ROUTES.app.root,
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireAuth>
        <RequireWorkspaceAccess>
          <AppLayout />
        </RequireWorkspaceAccess>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <WorkspaceHomeRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <DashboardPage />
            </Suspense>
          </WorkspaceHomeRoute>
        ),
      },
      {
        // Universal self-service account screen — every workspace actor.
        path: 'account',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <AccountPage />
          </Suspense>
        ),
      },
      {
        path: 'profile',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CandidateProfilePage />
          </Suspense>
        ),
      },
      // Reserved PRD modules — navigable previews (Tier A rich, Tier B light).
      {
        path: 'smart-profile',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <SmartProfilePreviewPage />
          </Suspense>
        ),
      },
      {
        path: 'job-matches',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <JobMatchesPreviewPage />
          </Suspense>
        ),
      },
      {
        path: 'applications',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <ApplicationsPreviewPage />
          </Suspense>
        ),
      },
      {
        path: 'employer',
        element: (
          <RequireActorTypes allowed={['employer', 'admin']}>
            <Suspense fallback={<SuspenseFallback />}>
              <EmployerDashboardPage />
            </Suspense>
          </RequireActorTypes>
        ),
      },
      {
        path: 'company',
        element: (
          <RequireActorTypes allowed={['employer', 'admin']}>
            <Suspense fallback={<SuspenseFallback />}>
              <CompanyPlaceholderPage />
            </Suspense>
          </RequireActorTypes>
        ),
      },
      {
        path: 'company/applications',
        element: (
          <RequireActorTypes allowed={['employer', 'admin']}>
            <Suspense fallback={<SuspenseFallback />}>
              <CompanyApplicationsPage />
            </Suspense>
          </RequireActorTypes>
        ),
      },
      {
        path: 'company/candidates',
        element: (
          <RequireActorTypes allowed={['employer', 'admin']}>
            <Suspense fallback={<SuspenseFallback />}>
              <CandidateSearchPage />
            </Suspense>
          </RequireActorTypes>
        ),
      },
      {
        path: 'company/profile',
        element: (
          <RequireActorTypes allowed={['employer', 'admin']}>
            <Suspense fallback={<SuspenseFallback />}>
              <CompanyProfilePage />
            </Suspense>
          </RequireActorTypes>
        ),
      },
      {
        path: 'admin',
        element: (
          <RequirePermission permission="metrics.read">
            <Suspense fallback={<SuspenseFallback />}>
              <AdminDashboardPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <RequirePermission permission="users.read">
            <Suspense fallback={<SuspenseFallback />}>
              <AdminUsersPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'admin/roles',
        element: (
          <RequirePermission permission="roles.read">
            <Suspense fallback={<SuspenseFallback />}>
              <AdminRolesPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'admin/plans',
        element: (
          <RequirePermission permission="plans.read">
            <Suspense fallback={<SuspenseFallback />}>
              <AdminPlansPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'admin/ai-usage',
        element: (
          <RequirePermission permission="metrics.read">
            <Suspense fallback={<SuspenseFallback />}>
              <AdminAiUsagePage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'admin/settings',
        element: (
          <RequirePermission permission="settings.read">
            <Suspense fallback={<SuspenseFallback />}>
              <AdminSettingsPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'admin/early-access',
        element: (
          <RequirePermission permission="early-access.read">
            <Suspense fallback={<SuspenseFallback />}>
              <EarlyAccessAdminPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'admin/audit-logs',
        element: (
          <RequirePermission permission="audit-logs.read">
            <Suspense fallback={<SuspenseFallback />}>
              <AuditLogPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <SettingsPlaceholderPage />
          </Suspense>
        ),
      },
      {
        path: 'analytics',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <AnalyticsPlaceholderPage />
          </Suspense>
        ),
      },
      {
        path: 'integrations',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <IntegrationsPlaceholderPage />
          </Suspense>
        ),
      },
    ],
  },
];
